// Connector geometry (spec §5.6). Connectors store endpoints (attached object
// id + normalised anchor, or a free point), never absolute paths: the path is
// derived from the current objects, so it follows moves on every client with
// no extra ops. ConnectorGeometry caches paths and tracks which connectors
// depend on which objects.
import type { BoardObject, Connector, Endpoint } from '../../shared/types.ts';
import type { Store } from '../model/store.ts';
import { distToSegmentSq, fromLocal, rotatePoint, toLocal, type Rect } from '../util/geometry.ts';

export const CONNECTOR_WIDTH = 2;
export const ARROW_SIZE = 12;
const ELBOW_MARGIN = 24;

type Dir = [number, number];
interface End { x: number; y: number; dir: Dir | null }

/** Side midpoints, in anchor coords. Connections snap to these. */
export const SIDE_ANCHORS: [number, number][] = [[0.5, 0], [1, 0.5], [0.5, 1], [0, 0.5]];

/** The side-midpoint anchor of `target` nearest to world point (wx, wy). */
export function nearestAnchor(target: BoardObject, wx: number, wy: number): [number, number] {
  const [lx, ly] = toLocal(target, wx, wy);
  let best = SIDE_ANCHORS[0]!, bestD = Infinity;
  for (const a of SIDE_ANCHORS) {
    const d = (a[0] * target.w - lx) ** 2 + (a[1] * target.h - ly) ** 2;
    if (d < bestD) { bestD = d; best = a; }
  }
  return [best[0], best[1]];
}

export function anchorPoint(target: BoardObject, anchor: readonly [number, number]): [number, number] {
  return fromLocal(target, anchor[0] * target.w, anchor[1] * target.h);
}

/** Axis-aligned direction pointing out of the side nearest the anchor, after rotation. */
function exitDir(target: BoardObject, [u, v]: readonly [number, number]): Dir {
  const d = [u, 1 - u, v, 1 - v];
  const side = d.indexOf(Math.min(...d));
  const local: Dir = side === 0 ? [-1, 0] : side === 1 ? [1, 0] : side === 2 ? [0, -1] : [0, 1];
  const [rx, ry] = rotatePoint(local[0], local[1], 0, 0, target.rotation);
  return Math.abs(rx) >= Math.abs(ry) ? [Math.sign(rx), 0] : [0, Math.sign(ry)];
}

function resolve(ep: Endpoint, get: (id: string) => BoardObject | undefined, fallback: [number, number]): End {
  if ('point' in ep) return { x: ep.point[0], y: ep.point[1], dir: null };
  const t = get(ep.objectId);
  // Target missing (e.g. deleted concurrently by someone else): keep the last known spot.
  if (!t) return { x: fallback[0], y: fallback[1], dir: null };
  const [x, y] = anchorPoint(t, ep.anchor);
  return { x, y, dir: exitDir(t, ep.anchor) };
}

/** World position of one end of a connector. */
export function endpointPosition(c: Connector, which: 'from' | 'to', get: (id: string) => BoardObject | undefined): [number, number] {
  const e = which === 'from' ? resolve(c.from, get, [c.x, c.y]) : resolve(c.to, get, [c.x + c.w, c.y + c.h]);
  return [e.x, e.y];
}

const towards = (p: End, q: End): Dir => {
  const dx = q.x - p.x, dy = q.y - p.y;
  return Math.abs(dx) >= Math.abs(dy) ? [Math.sign(dx) || 1, 0] : [0, Math.sign(dy) || 1];
};

/** Polyline for a connector, in world coords. */
export function connectorPoints(c: Connector, get: (id: string) => BoardObject | undefined): [number, number][] {
  const a = resolve(c.from, get, [c.x, c.y]);
  const b = resolve(c.to, get, [c.x + c.w, c.y + c.h]);
  if (c.routing === 'straight') return [[a.x, a.y], [b.x, b.y]];

  // Elbow: leave each end along its exit direction, then join with an
  // orthogonal dog-leg through the midpoint.
  const da = a.dir ?? towards(a, b);
  const db = b.dir ?? towards(b, a);
  const a1: [number, number] = [a.x + da[0] * ELBOW_MARGIN, a.y + da[1] * ELBOW_MARGIN];
  const b1: [number, number] = [b.x + db[0] * ELBOW_MARGIN, b.y + db[1] * ELBOW_MARGIN];
  const pts: [number, number][] = [[a.x, a.y], a1];
  if (da[0] !== 0) {
    const mx = (a1[0] + b1[0]) / 2;
    pts.push([mx, a1[1]], [mx, b1[1]]);
  } else {
    const my = (a1[1] + b1[1]) / 2;
    pts.push([a1[0], my], [b1[0], my]);
  }
  pts.push(b1, [b.x, b.y]);
  return tidy(pts);
}

/** Drops repeated points and merges collinear runs. */
function tidy(pts: [number, number][]): [number, number][] {
  const out: [number, number][] = [];
  for (const p of pts) {
    const last = out[out.length - 1];
    if (last && Math.abs(last[0] - p[0]) < 1e-6 && Math.abs(last[1] - p[1]) < 1e-6) continue;
    const prev = out[out.length - 2];
    if (last && prev) {
      const cross = (last[0] - prev[0]) * (p[1] - last[1]) - (last[1] - prev[1]) * (p[0] - last[0]);
      const dot = (last[0] - prev[0]) * (p[0] - last[0]) + (last[1] - prev[1]) * (p[1] - last[1]);
      if (Math.abs(cross) < 1e-6 && dot >= 0) out.pop();
    }
    out.push(p);
  }
  return out;
}

export function pointsBounds(pts: readonly [number, number][], pad: number): Rect {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const [x, y] of pts) {
    x0 = Math.min(x0, x); y0 = Math.min(y0, y);
    x1 = Math.max(x1, x); y1 = Math.max(y1, y);
  }
  return { x: x0 - pad, y: y0 - pad, w: x1 - x0 + 2 * pad, h: y1 - y0 + 2 * pad };
}

export function hitPolyline(pts: readonly [number, number][], wx: number, wy: number, tol: number): boolean {
  for (let i = 1; i < pts.length; i++) {
    if (distToSegmentSq(wx, wy, pts[i - 1]![0], pts[i - 1]![1], pts[i]![0], pts[i]![1]) <= tol * tol) return true;
  }
  return false;
}

export interface ConnectorShape { points: [number, number][]; bounds: Rect }

/** Cached connector paths plus the object → attached-connectors relation. */
export class ConnectorGeometry {
  private readonly store: Store;
  private readonly cache = new Map<string, ConnectorShape>();
  private readonly byTarget = new Map<string, Set<string>>(); // object id → connectors attached to it
  private readonly targets = new Map<string, string[]>();     // connector id → object ids it's attached to

  constructor(store: Store) {
    this.store = store;
  }

  /**
   * Updates bookkeeping for changed objects; returns connectors whose path
   * changed because something they're attached to changed.
   */
  update(changed: ReadonlySet<string>): Set<string> {
    const dependents = new Set<string>();
    for (const id of changed) {
      const o = this.store.get(id);
      if (this.targets.has(id) || o?.type === 'connector') {
        this.unlink(id);
        this.cache.delete(id);
        if (o?.type === 'connector') this.link(o);
      }
      for (const c of this.byTarget.get(id) ?? []) {
        if (changed.has(c)) continue;
        this.cache.delete(c);
        dependents.add(c);
      }
    }
    return dependents;
  }

  /** Connectors attached to any of `ids`. */
  attachedTo(ids: Iterable<string>): Set<string> {
    const out = new Set<string>();
    for (const id of ids) for (const c of this.byTarget.get(id) ?? []) out.add(c);
    return out;
  }

  shape(c: Connector): ConnectorShape {
    let s = this.cache.get(c.id);
    if (!s) {
      const points = connectorPoints(c, (id) => this.store.get(id));
      s = { points, bounds: pointsBounds(points, CONNECTOR_WIDTH + ARROW_SIZE) };
      this.cache.set(c.id, s);
    }
    return s;
  }

  private link(c: Connector): void {
    const ids = [c.from, c.to].flatMap((e) => ('objectId' in e ? [e.objectId] : []));
    this.targets.set(c.id, ids);
    for (const t of ids) {
      let set = this.byTarget.get(t);
      if (!set) this.byTarget.set(t, (set = new Set()));
      set.add(c.id);
    }
  }

  private unlink(id: string): void {
    for (const t of this.targets.get(id) ?? []) {
      const set = this.byTarget.get(t);
      set?.delete(id);
      if (set && !set.size) this.byTarget.delete(t);
    }
    this.targets.delete(id);
  }
}

/** Arrowhead triangle for a line arriving at `tip` from `from`. */
export function arrowPoints(from: readonly [number, number], tip: readonly [number, number]): [number, number][] {
  const a = Math.atan2(tip[1] - from[1], tip[0] - from[0]);
  const s = ARROW_SIZE, c = Math.cos(a), sn = Math.sin(a);
  return [
    [tip[0], tip[1]],
    [tip[0] - s * c + (s / 2) * sn, tip[1] - s * sn - (s / 2) * c],
    [tip[0] - s * c - (s / 2) * sn, tip[1] - s * sn + (s / 2) * c],
  ];
}

/** World bounds of a connector's drawn path, including arrowheads. */
export function connectorBounds(c: Connector, get: (id: string) => BoardObject | undefined): Rect {
  return pointsBounds(connectorPoints(c, get), CONNECTOR_WIDTH + ARROW_SIZE);
}
